export const STATUS_META = {
  OPEN: { label: "Open", bg: "#FFFBEB", border: "#FDE68A", color: "#B45309", dot: "#F59E0B" },
 "Follow-up": { label: "Follow-up", bg: "#F0F9FF", border: "#7DD3FC", color: "#0369A1", dot: "#0EA5E9" },
 "Booked": { label: "Booked", bg: "#ECFDF5", border: "#6EE7B7", color: "#065F46", dot: "#10B981" },
 "Closed – Invalid Number": { label: "Closed – Invalid Number", bg: "#F3F4F6", border: "#D1D5DB", color: "#374151", dot: "#6B7280" },
  Closed: { label: "Closed", bg: "#F3F4F6", border: "#D1D5DB", color: "#374151", dot: "#6B7280" },
};

// The statuses the Leads API still sends and counts (the KPI tiles, the pills, and Channels closing/booking a
// lead use them). They are not in the picker list above, but must still be looked up without crashing.
const LEGACY_STATUS_META = {
  NEW: { label: "New", bg: "#EFF6FF", border: "#BFDBFE", color: "#1D4ED8", dot: "#3B82F6" },
  ASSIGNED: { label: "Assigned", bg: "#F5F3FF", border: "#C4B5FD", color: "#6D28D9", dot: "#8B5CF6" },
  IN_PROGRESS: { label: "In progress", bg: "#FFF7ED", border: "#FDBA74", color: "#C2410C", dot: "#F97316" },
  WON: { label: "Booked", bg: "#ECFDF5", border: "#6EE7B7", color: "#065F46", dot: "#10B981" },
  LOST: { label: "Closed", bg: "#F3F4F6", border: "#D1D5DB", color: "#374151", dot: "#6B7280" },
  LOST_STALE: { label: "Closed", bg: "#F3F4F6", border: "#D1D5DB", color: "#374151", dot: "#6B7280" },
};

// The database may hand a status back in another spelling than the keys above ("Open" vs OPEN): match it
// ignoring capitals, so the pill, the status picker and the filters all find it.
export const canonicalStatus = (status) => {
  if (!status) return status;
  const lower = String(status).toLowerCase();
  return [...Object.keys(STATUS_META), ...Object.keys(LEGACY_STATUS_META)].find((k) => k.toLowerCase() === lower) || status;
};

// Any status -> its colours/label; never undefined.
export const statusMeta = (status) =>
  STATUS_META[canonicalStatus(status)] || LEGACY_STATUS_META[canonicalStatus(status)] || { label: status || "—", bg: "#F3F4F6", border: "#D1D5DB", color: "#374151", dot: "#9CA3AF" };

// The booking's platform — copied automatically from bookings.source, never
// picked by hand. Blank/no badge when a lead has no linked booking to copy
// it from (a true walk-in, phoned-in lead).
export const CHANNEL_LABELS = {
  WEB: "Web",
  APP: "App",
  // A lead created from the Channels tab's "Create Lead" chat button has no
  // booking to copy a WEB/APP platform off of, so it's tagged with this
  // instead (see lead.controller.js's create_lead: source_type override).
  Channels: "Channels",
};

// Why this lead exists/was flagged — the only thing REASON_LABELS covers now.
// 'channels_followup' is what the Channels tab's one-click "Create Lead"
// chat button sends (it has no reason dropdown of its own). 'manual' stays
// as the backend's generic fallback label for any other caller that omits
// lead_reason entirely (see lead.controller.js's create_lead).
export const REASON_LABELS = {
  payment_pending: "Payment Pending",
  cart_abandoned: "Cart Abandoned",
  service_unavailable: "Service Unavailable",
  price_objection: "Price Objection",
  callback_requested: "Callback Requested",
  channels_followup: "Channels Follow-up",
  manual: "Manual",
};

export const PRIORITY_META = {
  High: { color: "#B91C1C", dot: "#EF4444" },
  Medium: { color: "#B45309", dot: "#F59E0B" },
  Low: { color: "#6B7280", dot: "#9CA3AF" },
};

let nextLeadNum = 10242;
export const nextLeadRef = () => `LEAD-${nextLeadNum++}`;

export const SAMPLE_LEADS = [
  {
    id: "l1", ref: "LEAD-10241", customerName: "Anita Rao", phone: "+91 98450 22190",
    source: "payment_pending", status: "OPEN", assignedAgent: "Ravi S.", priority: "High",
    nextFollowUp: "Overdue", nextFollowUpSub: "was due 6h ago", overdue: true,
    service: "Deep Home Cleaning", cartValue: "₹2,450", attempts: "3 of 6",
    createdAt: "2 Sep 2026, 11:42 AM",
    timeline: [
      { id: "t1", kind: "auto", channel: "whatsapp", who: "WhatsApp · T3 nudge", when: "Today, 8:02 AM", msg: "\"Hi Anita, still thinking about your Deep Cleaning booking? We've saved your cart — complete it in 2 taps.\"", deliveryStatus: "Sent" },
      { id: "t2", kind: "manual", who: "Ravi S. — called customer", when: "Yesterday, 3:14 PM", msg: "Spoke briefly — she's comparing prices with another provider. Offered 10% off, said she'll decide by the weekend.", disposition: "Price Objection" },
      { id: "t3", kind: "auto", channel: "whatsapp", who: "WhatsApp · T2 nudge", when: "2 Sep, 5:42 PM", msg: "\"Still have questions about your booking? We're here to help — reply anytime.\"", deliveryStatus: "Read" },
      { id: "t4", kind: "system", who: "Lead created", when: "2 Sep, 11:42 AM", msg: "Auto-created 30 minutes after payment was left pending on Deep Home Cleaning checkout." },
    ],
  },
  {
    id: "l2", ref: "LEAD-10240", customerName: "Farhan Sheikh", phone: "+91 90210 44871",
    source: "cart_abandoned", status: "IN_PROGRESS", assignedAgent: "Priya N.", priority: "Medium",
    nextFollowUp: "Today, 4:30 PM", nextFollowUpSub: "WhatsApp · T2", overdue: false,
    service: "AC Repair", cartValue: "₹1,200", attempts: "1 of 6",
    createdAt: "3 Sep 2026, 9:10 AM",
    timeline: [
      { id: "t1", kind: "system", who: "Lead created", when: "3 Sep, 9:10 AM", msg: "Auto-created after cart was abandoned on AC Repair booking." },
    ],
  },
  {
    id: "l3", ref: "LEAD-10239", customerName: "Meera Iyer", phone: "+91 98805 12233",
    source: "manual", status: "NEW", assignedAgent: null, priority: "High",
    nextFollowUp: "Tomorrow, 10:00 AM", nextFollowUpSub: "Call-back requested", overdue: false,
    service: "—", cartValue: "—", attempts: "0 of 6",
    createdAt: "4 Sep 2026, 8:00 AM",
    timeline: [
      { id: "t1", kind: "manual", who: "Priya N. — note", when: "4 Sep, 8:00 AM", msg: "Customer called in asking for a callback about window cleaning pricing." },
    ],
  },
  {
    id: "l4", ref: "LEAD-10236", customerName: "Karthik Bose", phone: "+91 63645 90012",
    source: "price_objection", status: "OPEN", assignedAgent: "Ravi S.", priority: "Medium",
    nextFollowUp: "6 Sep, 9:00 AM", nextFollowUpSub: "WhatsApp · T4 (Day 3)", overdue: false,
    service: "Pest Control", cartValue: "₹899", attempts: "2 of 6",
    createdAt: "1 Sep 2026, 2:30 PM",
    timeline: [
      { id: "t1", kind: "system", who: "Lead created", when: "1 Sep, 2:30 PM", msg: "Auto-created 30 minutes after payment was left pending." },
    ],
  },
  {
    id: "l5", ref: "LEAD-10233", customerName: "Sneha Kulkarni", phone: "+91 99728 30144",
    source: "payment_pending", status: "ASSIGNED", assignedAgent: "Priya N.", priority: "Low",
    nextFollowUp: "Today, 2:00 PM", nextFollowUpSub: "First call pending", overdue: false,
    service: "Sofa Cleaning", cartValue: "₹1,650", attempts: "0 of 6",
    createdAt: "4 Sep 2026, 11:00 AM",
    timeline: [
      { id: "t1", kind: "system", who: "Lead created", when: "4 Sep, 11:00 AM", msg: "Auto-created 30 minutes after payment was left pending." },
    ],
  },
  {
    id: "l6", ref: "LEAD-10228", customerName: "Arvind Menon", phone: "+91 94480 77102",
    source: "service_unavailable", status: "WON", assignedAgent: "Ravi S.", priority: "Low",
    nextFollowUp: null, nextFollowUpSub: "Booked offline", overdue: false,
    service: "Plumbing", cartValue: "₹750", attempts: "2 of 6", conversionChannel: "Agent Call",
    createdAt: "28 Aug 2026, 4:20 PM",
    timeline: [
      { id: "t1", kind: "manual", who: "Ravi S. — called customer", when: "30 Aug, 5:00 PM", msg: "Booked directly with the crew on-site.", disposition: "Booked Offline" },
    ],
  },
  {
    id: "l7", ref: "LEAD-10219", customerName: "Divya Prasad", phone: "+91 88009 55671",
    source: "cart_abandoned", status: "LOST_STALE", assignedAgent: "Priya N.", priority: "Low",
    nextFollowUp: null, nextFollowUpSub: "Auto-expired, day 20", overdue: false,
    service: "Carpet Cleaning", cartValue: "₹550", attempts: "6 of 6",
    createdAt: "15 Aug 2026, 10:00 AM",
    timeline: [
      { id: "t1", kind: "system", who: "Auto-expired", when: "4 Sep, 12:00 AM", msg: "Cadence exhausted after 20 days with no response." },
    ],
  },
];
